import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatestWith, filter, map, Subject, switchMap } from 'rxjs';

import { ServiceStackTypeCategories } from '@zerops/models/service-stack';
import { Process, ProcessStatuses } from '@zerops/models/process';
import { AppVersion, AppVersionStatuses } from '@zerops/models/app-version';
import { hasProjectFullAccess } from '@zerops/models/client-user';
import { PipelineError } from '@zerops/models/error-backend';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { ProcessItemTranslations } from './process-item.translations';

@Component({
  selector: 'zui-process-item',
  templateUrl: './process-item.component.html',
  styleUrls: [ './process-item.component.scss' ]
})
export class ProcessItemComponent {

  /**
   * Dependencies.
   */
  #userEntity = inject(UserEntity);
  #projectEntity = inject(ProjectEntity);

  /**
   * Declared streams.
   */
  #projectId$ = new Subject<string>();

  /**
   * Sync properties.
   */
  processStatuses = ProcessStatuses;

  statusColorClassMap = {
    [ProcessStatuses.PENDING]: 'orange',
    [ProcessStatuses.RUNNING]: 'blue'
  };

  #data: Process;

  @Input()
  set data(v) {
    this.#projectId$.next(v?.projectId);
    this.#data = {
      ...v,
      serviceStacks: v?.serviceStacks.filter(((d) => d.serviceStackTypeInfo.serviceStackTypeCategory !== ServiceStackTypeCategories.Build))
    };
  }
  get data() {
    return this.#data;
  }

  @Input()
  pipelineErrors: PipelineError[];

  @Input()
  translations: ProcessItemTranslations;

  @Input()
  cancelLoading = false;

  @Input()
  hideCancel = false;

  @Input()
  makeContentScrollable = false;

  @Output()
  internalLinkClicked = new EventEmitter<void>();

  @Output()
  cancelClicked = new EventEmitter<string>();

  @Output()
  pipelineDetailClicked = new EventEmitter<{ appVersion: AppVersion; pipelineErrors: PipelineError[]; }>();

  @Output()
  buildCancelClicked = new EventEmitter<string>();

  actionsMap = {
    'instanceGroup.create': { isSystem: true, action: 'create', projectPrefix: 'in' },
    'instanceGroup.delete': { isSystem: true, action: 'delete', projectPrefix: 'from' },
    'project.create': { isSystem: true, action: 'create', projectPrefix: 'to' },
    'project.delete': { isSystem: true, action: 'delete', projectPrefix: 'from' },
    'project.syncPublicPortRouting': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'project.syncPublicHttpRouting': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'project.syncPublicHttpRoutingAndCreateL7HttpBalancerStack': { isSystem: true, action: 'create', projectPrefix: 'in' },
    'project.createL7HttpBalancerStack': { isSystem: true, action: 'create', projectPrefix: 'in' },
    'project.deleteL7HttpBalancerStack': { isSystem: true, action: 'delete', projectPrefix: 'from' },
    'instance.modeUpgrade': { isSystem: true, action: 'update', projectPrefix: 'in' },
    'stack.delete': { isSystem: false, action: 'delete', projectPrefix: 'from' },
    'stack.create': { isSystem: false, action: 'create', projectPrefix: 'in' },
    'stack.start': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.stop': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.restart': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.reload': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.deploy': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.updateUserData': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.disableSubdomainAccess': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.enableSubdomainAccess': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.updatePorts': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.nginxConfig': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'stack.build': { isSystem: false, action: 'create', projectPrefix: 'in' },
    'stack.createBuildStack': { isSystem: true, action: 'create', projectPrefix: 'in' },
    'stack.deleteBuildStack': { isSystem: true, action: 'delete', projectPrefix: 'in' },
    'stack.deleteBuildCache': { isSystem: true, action: 'delete', projectPrefix: 'in' },
    'instance.publicIpV4.request': { isSystem: false, action: 'standard', projectPrefix: 'in' },
    'instance.publicIpV4.release': { isSystem: false, action: 'standard', projectPrefix: 'in' }
  };

  /**
   * Async properties.
   */
  #activeClientUser$ = this.#userEntity.activeClientUser$.pipe(
    filter((d) => !!d)
  );
  #project$ = this.#projectId$.pipe(
    filter((d) => !!d),
    switchMap((projectId) => this.#projectEntity.entityById$(projectId)),
  );
  #hasProjectFullAccess$ = this.#project$.pipe(
    filter((d) => !!d),
    combineLatestWith(this.#activeClientUser$),
    map(([ project, clientUser ]) => hasProjectFullAccess(project, clientUser))
  );
  #isReadOnly$ = this.#hasProjectFullAccess$.pipe(
    map((hasFullAccess) => !hasFullAccess)
  );
  isReadOnly = toSignal(this.#isReadOnly$, { initialValue: true });

  get isBuildInProgress(): boolean {
    return [
      AppVersionStatuses.Building,
      AppVersionStatuses.WaitingToBuild,
      AppVersionStatuses.Uploading
    ].includes(this.data?.appVersion?.status);
  }

}
